// Compliance Center — platform staff govern the legal documents every company and
// employee accepts (Terms of Service, Privacy Policy, AI Usage Policy, …). View the
// catalogue + immutable version history, publish a new version (which makes the
// first-login ComplianceGate re-prompt everyone for re-acceptance), and watch
// platform-wide adoption. Read access: auditlog:read:all; publishing a version is
// shown only to platform admins/owners (feature-flag:manage) and is enforced again
// server-side (compliance-service requires the admin permission).
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, Card, Button, Modal, Form, Input, Select, Switch, DatePicker, Tag, Tabs,
  Typography, Space, Statistic, Row, Col, message, Tooltip,
} from 'antd';
import {
  FileProtectOutlined, PlusOutlined, ReloadOutlined, HistoryOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '../../app/hooks';
import { selectHasPermission } from '../../features/auth/authSlice';
import {
  getPlatformDocuments, getPlatformSummary, publishVersion,
  type LegalDocument, type LegalVersion, type AcceptanceSummary,
} from '../../services/complianceService';

const { Text, Title } = Typography;

// Document kinds platform staff can publish. Free text is also allowed (a brand-new
// doc_type), but these cover the standard set and keep slugs consistent.
const DOC_TYPES = [
  { value: 'terms_of_service', label: 'Terms of Service' },
  { value: 'privacy_policy', label: 'Privacy Policy' },
  { value: 'ai_usage_policy', label: 'AI Usage Policy' },
  { value: 'data_processing_agreement', label: 'Data Processing Agreement' },
  { value: 'cookie_policy', label: 'Cookie Policy' },
  { value: 'acceptable_use_policy', label: 'Acceptable Use Policy' },
];
const docLabel = (slug: string) =>
  DOC_TYPES.find((d) => d.value === slug)?.label ||
  slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());


const ComplianceCenterPage: React.FC = () => {
  const canPublish = useAppSelector(selectHasPermission('feature-flag:manage'));

  const [docs, setDocs] = useState<LegalDocument[]>([]);
  const [summary, setSummary] = useState<AcceptanceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishOpen, setPublishOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([getPlatformDocuments(), getPlatformSummary()]);
      setDocs(d);
      setSummary(s);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load compliance data.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openPublish = (docType?: string) => {
    form.resetFields();
    form.setFieldsValue({ docType, language: 'en', required: true });
    setPublishOpen(true);
  };

  const submitPublish = async () => {
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      await publishVersion(v.docType.trim(), {
        title: v.title.trim(),
        body: v.body,
        summary: v.summary?.trim() || undefined,
        language: v.language || 'en',
        required: v.required,
        effective_date: v.effective_date ? v.effective_date.toISOString() : null,
      });
      message.success(`Published a new version of ${docLabel(v.docType.trim())}.`);
      setPublishOpen(false);
      form.resetFields();
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not publish the version.');
    } finally {
      setSubmitting(false);
    }
  };

  const totalAccepted = useMemo(
    () => summary.reduce((n, s) => n + s.accepted_count, 0),
    [summary],
  );

  const docColumns = [
    {
      title: 'Document',
      dataIndex: 'doc_type',
      key: 'doc_type',
      render: (slug: string, d: LegalDocument) => (
        <Space direction="vertical" size={0}>
          <Text strong>{d.title || docLabel(slug)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{slug}</Text>
        </Space>
      ),
    },
    {
      title: 'Current version',
      dataIndex: 'current_version',
      key: 'current_version',
      render: (v: number) => <Tag color="blue">v{v}</Tag>,
    },
    {
      title: 'Required',
      dataIndex: 'required',
      key: 'required',
      render: (req: boolean) =>
        req ? <Tag color="gold">Required</Tag> : <Tag>Optional</Tag>,
    },
    {
      title: 'Versions',
      key: 'count',
      render: (_: unknown, d: LegalDocument) => (
        <Space size={4}>
          <HistoryOutlined />
          {d.versions.length}
        </Space>
      ),
    },
    ...(canPublish
      ? [{
          title: 'Action',
          key: 'action',
          render: (_: unknown, d: LegalDocument) => (
            <Button size="small" icon={<PlusOutlined />} onClick={() => openPublish(d.doc_type)}>
              New version
            </Button>
          ),
        }]
      : []),
  ];

  const versionColumns = [
    { title: 'Version', dataIndex: 'version', key: 'version', render: (v: number) => `v${v}` },
    { title: 'Language', dataIndex: 'language', key: 'language', render: (l: string) => l?.toUpperCase() },
    { title: 'Title', dataIndex: 'title', key: 'title' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => (
        <Tag color={s === 'published' ? 'green' : s === 'archived' ? 'default' : 'orange'}>{s}</Tag>
      ),
    },
    { title: 'Published', dataIndex: 'publish_date', key: 'publish_date', render: fmtDate },
    { title: 'Effective', dataIndex: 'effective_date', key: 'effective_date', render: fmtDate },
  ];

  const summaryColumns = [
    {
      title: 'Document',
      dataIndex: 'title',
      key: 'title',
      render: (t: string, s: AcceptanceSummary) => t || docLabel(s.doc_type),
    },
    { title: 'Current', dataIndex: 'version', key: 'version', render: (v: number) => <Tag color="blue">v{v}</Tag> },
    {
      title: 'Accepted by',
      dataIndex: 'accepted_count',
      key: 'accepted_count',
      render: (n: number) => `${n} ${n === 1 ? 'user' : 'users'}`,
    },
    {
      title: 'Across tenants',
      dataIndex: 'tenant_count',
      key: 'tenant_count',
      render: (n?: number) => n ?? 0,
    },
  ];

  return (
    <div className="p-1">
      <div className="flex items-center justify-between mb-4">
        <Space align="center">
          <FileProtectOutlined style={{ fontSize: 22 }} />
          <Title level={3} style={{ margin: 0 }}>Compliance Center</Title>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          {canPublish && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openPublish()}>
              Publish version
            </Button>
          )}
        </Space>
      </div>

      <Text type="secondary">
        Publishing a new version of a required document re-prompts every company and
        employee to accept it at their next sign-in.
      </Text>

      <Row gutter={16} className="mt-4 mb-4">
        <Col span={8}>
          <Card><Statistic title="Legal documents" value={docs.length} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="Required documents" value={docs.filter((d) => d.required).length} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="Total acceptances (current versions)" value={totalAccepted} /></Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="docs"
        items={[
          {
            key: 'docs',
            label: 'Documents',
            children: (
              <Card>
                <Table<LegalDocument>
                  rowKey="doc_type"
                  loading={loading}
                  dataSource={docs}
                  columns={docColumns as any}
                  pagination={false}
                  expandable={{
                    expandedRowRender: (d) => (
                      <Table<LegalVersion>
                        rowKey="id"
                        size="small"
                        dataSource={d.versions}
                        columns={versionColumns as any}
                        pagination={false}
                      />
                    ),
                    rowExpandable: (d) => d.versions.length > 0,
                  }}
                />
              </Card>
            ),
          },
          {
            key: 'adoption',
            label: 'Adoption',
            children: (
              <Card>
                <Table<AcceptanceSummary>
                  rowKey={(s) => `${s.doc_type}-${s.version}`}
                  loading={loading}
                  dataSource={summary}
                  columns={summaryColumns as any}
                  pagination={false}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title="Publish a new document version"
        open={publishOpen}
        onOk={submitPublish}
        onCancel={() => setPublishOpen(false)}
        confirmLoading={submitting}
        okText="Publish"
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark>
          <Form.Item
            name="docType"
            label="Document type"
            rules={[{ required: true, message: 'Choose or enter a document type.' }]}
            tooltip="Pick a standard type, or type a new slug (e.g. cookie_policy) to create one."
          >
            <Select
              showSearch
              allowClear
              placeholder="terms_of_service"
              options={DOC_TYPES}
              mode={undefined}
              // Allow a free-text slug for new document kinds.
              onSearch={() => {}}
              filterOption={(input, opt) =>
                (opt?.value as string).toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Enter a title.' }]}
          >
            <Input placeholder="Terms of Service" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="language" label="Language" initialValue="en">
                <Select
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'sw', label: 'Kiswahili' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effective_date" label="Effective date (optional)">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="summary" label="Summary (optional)" tooltip="Shown as a short note about this version.">
            <Input placeholder="What changed in this version" />
          </Form.Item>
          <Form.Item
            name="body"
            label="Document body"
            rules={[{ required: true, message: 'Enter the document text.' }]}
          >
            <Input.TextArea rows={10} placeholder="Full legal text…" />
          </Form.Item>
          <Form.Item
            name="required"
            label={
              <Tooltip title="Required documents must be accepted before using the app.">
                <span>Required for everyone</span>
              </Tooltip>
            }
            valuePropName="checked"
            initialValue
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ComplianceCenterPage;
